/* ============================================================
   Lagomlo — leaderboard.js (Shopify theme)
   Warmth Leaderboard page controller (port of preview/js/leaderboard.js):
   monthly / quarterly / yearly tabs, Top-10 ranking by confirmed-delivered
   orders, tier ladder render and the "my rank" box.

   Data (DEMO): the ranking is computed by window.Loyalty (loyalty.js) from
   the 'lagomlo.orders' localStorage mirror — orders with status 'Delivered'
   and a deliveredAt timestamp inside the period. A small demo seed is written
   on first visit so the page isn't empty. Production needs a backend/app that
   aggregates confirmed orders across all customers.

   Session: leaderboard.liquid renders the customer email + warmth metafields
   into data attributes ('' = guest). All dynamic strings come from the
   #leaderboardI18n JSON blob (locale file).

   Exposed: window.Leaderboard
   ============================================================ */
(function (global) {
  'use strict';

  var ORDERS_LS = 'lagomlo.orders';
  var SEED_LS = 'lagomlo.demoOrdersSeeded';

  function el(id) { return document.getElementById(id); }

  function readLS(key, fallback) {
    try {
      var raw = global.localStorage.getItem(key);
      return raw === null ? fallback : JSON.parse(raw);
    } catch (e) { return fallback; }
  }
  function writeLS(key, value) {
    try { global.localStorage.setItem(key, JSON.stringify(value)); } catch (e) {}
  }

  function escHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function fmt(tpl, map) {
    var s = String(tpl == null ? '' : tpl);
    Object.keys(map || {}).forEach(function (k) {
      s = s.split('{{ ' + k + ' }}').join(String(map[k]));
    });
    return s;
  }

  function i18n() {
    var node = el('leaderboardI18n');
    if (!node) return {};
    try { return JSON.parse(node.textContent) || {}; } catch (e) { return {}; }
  }

  function root() { return document.querySelector('[data-leaderboard]'); }

  function currentEmail() {
    var r = root();
    if (!r) return '';
    return (r.getAttribute('data-leaderboard-customer-email') || '').trim();
  }

  function loginUrl() {
    var routes = (global.Lagomlo && global.Lagomlo.routes) || {};
    return (routes.root || '/') + 'account/login';
  }

  function periodWord(period) {
    var t = i18n();
    if (period === 'monthly') return t.period_month || 'month';
    if (period === 'quarterly') return t.period_quarter || 'quarter';
    return t.period_year || 'year';
  }

  /* ---------- Demo seed (first visit only; drop for production) ---------- */
  function seedDemoOrders() {
    if (readLS(SEED_LS, false)) return;
    var orders = readLS(ORDERS_LS, []);
    if (orders.length) { writeLS(SEED_LS, true); return; } // someone already wrote real/demo data
    var now = Date.now();
    var DAY = 86400000;
    var ago = function (days) { return new Date(now - days * DAY).toISOString(); };
    var demo = [
      { id: 'LAG-9001', name: 'Ava',    email: 'ava@lagomlo.com',    status: 'Delivered', deliveredAt: ago(2)   },
      { id: 'LAG-9002', name: 'Leo',    email: 'leo@lagomlo.com',    status: 'Delivered', deliveredAt: ago(4)   },
      { id: 'LAG-9003', name: 'Mia',    email: 'mia@lagomlo.com',    status: 'Delivered', deliveredAt: ago(7)   },
      { id: 'LAG-9004', name: 'Noah',   email: 'noah@lagomlo.com',   status: 'Delivered', deliveredAt: ago(21)  },
      { id: 'LAG-9005', name: 'Emma',   email: 'emma@lagomlo.com',   status: 'Delivered', deliveredAt: ago(35)  },
      { id: 'LAG-9006', name: 'Liam',   email: 'liam@lagomlo.com',   status: 'Delivered', deliveredAt: ago(45)  },
      { id: 'LAG-9007', name: 'Olivia', email: 'olivia@lagomlo.com', status: 'Delivered', deliveredAt: ago(90)  },
      { id: 'LAG-9008', name: 'Ava',    email: 'ava@lagomlo.com',    status: 'Delivered', deliveredAt: ago(140) }
    ];
    writeLS(ORDERS_LS, orders.concat(demo));
    writeLS(SEED_LS, true);
  }

  /* ---------- Ranking render ---------- */
  function renderList(period) {
    var listEl = el('lbList');
    var labelEl = el('lbPeriodLabel');
    var mineEl = el('lbMine');
    if (!listEl) return;
    var t = i18n();
    var L = global.Loyalty || null;
    if (labelEl) labelEl.textContent = L ? L.periodLabel(period) : period;
    var list = L ? L.ranking(period) : [];
    var email = currentEmail();

    if (!list.length) {
      listEl.innerHTML = '<p class="text-soft">' + escHtml(fmt(t.empty || 'No confirmed orders yet this {{ period }}. Confirm receipt of an order in your account to start climbing! 🧗', { period: periodWord(period) })) + '</p>';
      if (mineEl) mineEl.hidden = true;
      return;
    }

    var top3 = ['🥇', '🥈', '🥉'];
    listEl.innerHTML = '<div class="lb-rows">' + list.slice(0, 10).map(function (u, i) {
      var rank = i < 3 ? top3[i] : (i + 1);
      var isMe = email && email.toLowerCase() === u.email;
      return (
        '<div class="lb-row' + (isMe ? ' is-me' : '') + '">' +
        '  <span class="lb-rank">' + rank + '</span>' +
        '  <span class="lb-name">' + escHtml(u.name) + (isMe ? ' <em>' + escHtml(t.you_suffix || '(you)') + '</em>' : '') + '</span>' +
        '  <span class="lb-tier">' + escHtml(u.tierName) + '</span>' +
        '  <span class="lb-orders">' + escHtml(fmt(u.orders === 1 ? (t.orders_count_one || '{{ count }} order') : (t.orders_count_other || '{{ count }} orders'), { count: u.orders })) + '</span>' +
        '  <span class="lb-points">' + escHtml(fmt(t.pts || '{{ count }} pts', { count: u.points })) + '</span>' +
        '</div>'
      );
    }).join('') + '</div>';

    // "my rank" box
    if (mineEl) {
      if (email) {
        var myIdx = -1;
        for (var i = 0; i < list.length; i++) {
          if (list[i].email === email.toLowerCase()) { myIdx = i; break; }
        }
        mineEl.hidden = false;
        if (myIdx >= 0) {
          var my = list[myIdx];
          var rankTpl = my.orders === 1
            ? (t.mine_rank_one || 'You are <strong>#{{ rank }}</strong> with {{ count }} confirmed order — {{ points }} Warmth Points · <strong>{{ tier }}</strong> tier. Keep going! 🔥')
            : (t.mine_rank_other || 'You are <strong>#{{ rank }}</strong> with {{ count }} confirmed orders — {{ points }} Warmth Points · <strong>{{ tier }}</strong> tier. Keep going! 🔥');
          mineEl.innerHTML = fmt(rankTpl, { rank: myIdx + 1, count: my.orders, points: my.points, tier: my.tierName });
        } else {
          mineEl.innerHTML = escHtml(fmt(t.mine_empty || 'You haven\'t confirmed any orders this {{ period }} yet. Confirm receipt of an order to join the leaderboard!', { period: periodWord(period) }));
        }
      } else {
        mineEl.hidden = false;
        mineEl.innerHTML = '<a href="' + loginUrl() + '">' + escHtml(t.mine_signin || 'Sign in to see your rank.') + '</a>';
      }
    }
  }

  /* ---------- Tier ladder render ---------- */
  function renderTiers() {
    var tiersEl = el('lbTiers');
    var L = global.Loyalty || null;
    if (!tiersEl || !L || !L.WARMTH_TIERS) return;
    var t = i18n();
    var myIdx = -1;
    var email = currentEmail();
    if (email) {
      var r = root();
      var base = r ? (parseInt(r.getAttribute('data-leaderboard-confirmed') || '0', 10) || 0) : 0;
      var local = (L.localConfirmedCount && L.localConfirmedCount(email)) || 0;
      myIdx = L.tierFor(base + local).index;
    }
    tiersEl.innerHTML = '<div class="lb-tiers__grid">' + L.WARMTH_TIERS.map(function (tier, i) {
      var icon = i >= 9 ? '🏆' : i >= 5 ? '🔥' : i >= 2 ? '☀️' : '🧣';
      var reached = myIdx >= i;
      return (
        '<div class="card lb-tier' + (reached ? ' is-reached' : '') + (myIdx === i ? ' is-current' : '') + '">' +
        '  <div class="lb-tier__icon">' + icon + '</div>' +
        '  <div class="lb-tier__name">' + escHtml(tier.name) + '</div>' +
        '  <div class="lb-tier__orders">' + escHtml(fmt(tier.orders === 1 ? (t.tier_orders_one || '{{ count }} order') : (t.tier_orders_other || '{{ count }} orders'), { count: tier.orders })) + '</div>' +
        (myIdx === i ? '<span class="badge">' + escHtml(t.tier_here || 'You are here') + '</span>' : '') +
        '</div>'
      );
    }).join('') + '</div>';
  }

  function bindTabs() {
    var tabs = document.querySelectorAll('.lb-tab');
    if (!tabs.length) return;
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        tabs.forEach(function (t) {
          t.classList.toggle('is-active', t === tab);
          t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
        });
        renderList(tab.getAttribute('data-period'));
      });
    });
  }

  function init() {
    if (!el('lbList')) return; // not on the leaderboard page
    seedDemoOrders();
    renderTiers();
    renderList('monthly');
    bindTabs();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  global.Leaderboard = {
    init: init,
    renderList: renderList,
    renderTiers: renderTiers,
    ranking: function (period) { return (global.Loyalty || null) ? global.Loyalty.ranking(period) : []; },
    periodLabel: function (period) { return (global.Loyalty || null) ? global.Loyalty.periodLabel(period) : period; }
  };
})(window);
